import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getBillingSummary, type BillingSummary } from '../api/billingReader'

const ACTIVE_STATUSES = new Set(['active', 'trialing'])
const FAILURE_STATUSES = new Set(['payment_failed', 'past_due'])

type SuccessState = 'loading' | 'finalizing' | 'success' | 'pending' | 'failed'

function isSummaryActive(summary: BillingSummary, subscriptionId: string): boolean {
  const status = String(summary?.plan?.status || '').toLowerCase()
  if (!ACTIVE_STATUSES.has(status)) return false
  if (!subscriptionId) return true
  return String(summary?.plan?.stripe_subscription_id || '') === subscriptionId
}

function isSummaryFailed(summary: BillingSummary, redirectStatus: string): boolean {
  const status = String(summary?.plan?.status || '').toLowerCase()
  if (status === 'incomplete' && redirectStatus === 'succeeded') return false
  return FAILURE_STATUSES.has(status) || status === 'incomplete'
}

export default function SubscribeSuccess() {
  const [summary, setSummary] = useState<BillingSummary | null>(null)
  const [state, setState] = useState<SuccessState>('loading')
  const [params] = useSearchParams()
  const subscriptionId = params.get('subscription_id') || ''
  const tier = params.get('tier') || 'creator'
  const redirectStatus = String(params.get('redirect_status') || '').toLowerCase()

  const refreshSummary = async (): Promise<BillingSummary | null> => {
    try {
      const data = await getBillingSummary()
      setSummary(data)
      if (isSummaryFailed(data, redirectStatus)) {
        setState('failed')
        return data
      }
      if (isSummaryActive(data, subscriptionId)) {
        setState('success')
        return data
      }
      return data
    } catch {
      return null
    }
  }

  useEffect(() => {
    let active = true
    let pollCount = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      const data = await refreshSummary()
      if (!active) return
      if (data && (isSummaryFailed(data, redirectStatus) || isSummaryActive(data, subscriptionId))) return
      if (pollCount >= 15) {
        setState('pending')
        return
      }
      setState('finalizing')
      pollCount += 1
      timer = setTimeout(() => {
        void poll()
      }, 2000)
    }

    void poll()

    return () => {
      active = false
      if (timer) clearTimeout(timer)
    }
  }, [subscriptionId, redirectStatus])

  const runRefresh = async () => {
    setState('loading')
    const data = await refreshSummary()
    if (data && !isSummaryFailed(data, redirectStatus) && !isSummaryActive(data, subscriptionId)) {
      setState('pending')
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6">
          {state === 'success' ? (
            <>
              <h1 className="text-2xl font-extrabold">Subscription complete</h1>
              <p className="mt-2 text-white/80">
                {`You are now on ${summary?.plan?.name || (tier === 'creator_pro' ? 'Creator Pro' : 'Creator')}.`}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/publish/new" className="rounded-xl bg-brand px-4 py-2 font-semibold">
                  Go to Pixe Publish
                </Link>
                <Link to="/settings/billing" className="rounded-xl border border-white/20 px-4 py-2 text-white/85">
                  View billing
                </Link>
              </div>
            </>
          ) : null}
          {state === 'failed' ? (
            <>
              <h1 className="text-2xl font-extrabold text-red-200">Payment failed</h1>
              <p className="mt-2 text-white/80">Your payment did not complete. Please try again.</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to={`/subscribe?tier=${tier}`} className="rounded-xl bg-brand px-4 py-2 font-semibold">
                  Try again
                </Link>
                <Link to="/settings/billing" className="rounded-xl border border-white/20 px-4 py-2 text-white/85">
                  Billing portal
                </Link>
              </div>
            </>
          ) : null}
          {state === 'loading' || state === 'finalizing' ? (
            <>
              <h1 className="text-2xl font-extrabold">Finalizing subscription...</h1>
              <p className="mt-2 text-white/80">We are waiting for payment confirmation from Stripe.</p>
            </>
          ) : null}
          {state === 'pending' ? (
            <>
              <h1 className="text-2xl font-extrabold">Pending confirmation</h1>
              <p className="mt-2 text-white/80">Your payment is still processing. Refresh status or check Billing portal.</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button type="button" onClick={() => void runRefresh()} className="rounded-xl bg-brand px-4 py-2 font-semibold">
                  Refresh
                </button>
                <Link to="/settings/billing" className="rounded-xl border border-white/20 px-4 py-2 text-white/85">
                  Billing portal
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </main>
    </div>
  )
}
